// The job runner and the MCP server used to be two separate processes —
// now merged into one (see automation/mcp-server/server.mjs) so this
// shares the exact same automation engine as Carrier Access.
// IVANS Monitor can live on a different server than Carrier Access (REACT_APP_IVANS_URL); it falls back to the same one.
const AUTOMATION_BASE = process.env.REACT_APP_IVANS_URL || process.env.REACT_APP_AUTOMATION_URL || 'http://localhost:4791';

export interface MonitoredAgency {
  name: string;
  accountCode: string;
  credentialProfile: string;
  nowcertsAgencyDatabaseId?: string;
  /** Never the real key — just whether one is stored for this agency (see server-side redaction). */
  nowcertsApiKeyConfigured?: boolean;
}

export interface CredentialProfileSummary {
  name: string;
  isDefault: boolean;
}

export interface IvansSettings {
  thresholdHours: number;
  recipients: string[];
  nowcertsMatchWindowHours: number;
}

export interface StuckFile {
  fileId: string;
  classCode: string;
  carrier: string;
  agencyCode: string;
  deliveredAt: string;
  hoursStuck: number;
  isReescalation: boolean;
}

export interface UnconfirmedFile {
  fileId: string;
  carrier: string;
  agencyCode: string;
  statusDate: string;
}

export interface PerAgencyCheckResult {
  accountCode: string;
  agencyName: string;
  totalCount: number;
  receivedCount: number;
  stuck: StuckFile[];
  unconfirmed: UnconfirmedFile[];
  /** Whether the NowCerts cross-check actually ran for this agency this
   * time — distinct from unconfirmed.length === 0, which is ambiguous
   * between "ran, all clean" and "never configured/ran at all". */
  nowcertsChecked: boolean;
  error?: string;
}

export interface HistoryRecord {
  fileId: string;
  carrier: string;
  agencyCode: string;
  agencyName: string;
  alertedAt: string;
  [key: string]: unknown;
}

export interface AgencySearchResult {
  account: string;
  name: string;
  city: string;
  state: string;
  zip: string;
  notes: string;
}

export type ScheduleConfig =
  | { mode: 'interval'; minutes: number }
  | { mode: 'daily'; hour: number; minute: number };

export interface ScheduleStatus {
  enabled: boolean;
  supported: boolean;
  config?: ScheduleConfig;
}

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(`${AUTOMATION_BASE}${path}`);
  if (!res.ok) throw new Error(`${path} failed (${res.status})`);
  return res.json();
}

async function postJson<T>(path: string, body?: object): Promise<T> {
  const res = await fetch(`${AUTOMATION_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body ?? {}),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `${path} failed (${res.status})`);
  }
  return res.json();
}

export const getAgencies = () => getJson<MonitoredAgency[]>('/ivans/agencies');

export interface AddAgencyInput {
  name: string;
  accountCode: string;
  nowcertsAgencyDatabaseId?: string;
  nowcertsApiKey?: string;
  credentialProfile?: string;
  username?: string;
  password?: string;
}
export const addAgency = (input: AddAgencyInput) => postJson<{ ok: boolean }>('/ivans/agencies', input);
export const removeAgency = (name: string) => postJson<{ ok: boolean }>('/ivans/agencies/remove', { name });

export const getCredentialProfiles = () => getJson<CredentialProfileSummary[]>('/ivans/credentials');
export const addCredentialProfile = (name: string, username: string, password: string) =>
  postJson<{ ok: boolean }>('/ivans/credentials', { name, username, password });
export const removeCredentialProfile = (name: string) => postJson<{ ok: boolean }>('/ivans/credentials/remove', { name });

export const getSettings = () => getJson<IvansSettings>('/ivans/settings');
export const saveSettings = (settings: IvansSettings) => postJson<{ ok: boolean }>('/ivans/settings', settings);

export const getHistory = () => getJson<HistoryRecord[]>('/ivans/history');
export const clearHistory = () => postJson<{ ok: boolean }>('/ivans/history/clear');

export const getScheduleStatus = () => getJson<ScheduleStatus>('/ivans/schedule/status');
export const enableSchedule = (config: ScheduleConfig) => postJson<ScheduleStatus>('/ivans/schedule/enable', config);
export const disableSchedule = () => postJson<ScheduleStatus>('/ivans/schedule/disable');

// Check Now / Find Agency both run through the same generic job runner
// Carrier Access uses (see automationService.ts) — duplicated minimally
// here rather than importing cross-feature, since the two features are
// meant to stay independently readable.
export interface JobStatus {
  status: 'running' | 'needs_attention' | 'completed' | 'failed';
  log: string;
  resultPath: string | null;
  policiesJson: unknown;
  attentionMessage: string | null;
  resumed: boolean;
  label: string;
}

async function runJob(jobKey: string, extraArgs: string[] = []): Promise<string> {
  const response = await fetch(`${AUTOMATION_BASE}/run`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ jobKey, extraArgs }),
  });
  if (!response.ok) throw new Error(`Failed to start ${jobKey} (${response.status})`);
  const { runId } = await response.json();
  return runId;
}

async function getStatus(runId: string): Promise<JobStatus> {
  const response = await fetch(`${AUTOMATION_BASE}/status/${runId}`);
  if (!response.ok) throw new Error(`Unknown run ${runId}`);
  return response.json();
}

export async function runIvansJobAndWait(
  jobKey: string,
  extraArgs: string[] = [],
  onUpdate?: (status: JobStatus) => void,
): Promise<JobStatus> {
  const runId = await runJob(jobKey, extraArgs);
  for (;;) {
    const status = await getStatus(runId);
    onUpdate?.(status);
    if (status.status === 'completed' || status.status === 'failed') return status;
    await new Promise((resolve) => setTimeout(resolve, 1500));
  }
}

/** Pulls the RESULT_JSON:{...} marker line out of a completed job's raw log — same convention checkAllAgencies.ts/searchAgency.ts use. */
export function parseResultJson<T>(log: string): T | null {
  const match = log.match(/RESULT_JSON:(.+)/);
  if (!match) return null;
  try {
    return JSON.parse(match[1]) as T;
  } catch {
    return null;
  }
}

/** Shared across every IVANS Monitor tab that shows a timestamp — turns a raw ISO string into something readable instead of the full "2026-09-15T17:17:15.014782" form. */
export function formatDate(value: string | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString(undefined, { month: 'numeric', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}
